import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getMedia, deleteMedia, type MediaItem } from '../../shared/api/media';
import { useAuth } from '../../shared/auth/AuthContext';
import { getAdminLoginPath, getAdminMediaListPath, getAdminPostsPath } from '../../shared/config/env';
import AdminSidebar from '../components/AdminSidebar';
import './MediaDetail.css';

function MediaDetail() {
  const { mediaId } = useParams<{ mediaId: string }>();
  const [media, setMedia] = useState<MediaItem | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const adminLoginPath = getAdminLoginPath();
  const adminMediaListPath = getAdminMediaListPath();
  const adminPostsPath = getAdminPostsPath();

  useEffect(() => {
    const fetchMedia = async () => {
      if (!mediaId) {
        setError('メディアIDが指定されていません');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await getMedia(mediaId);
        setMedia(data);
      } catch (err) {
        if (err instanceof Error && (err as any).isAuthError) {
          // 認証エラーの場合、ログアウトしてログイン画面にリダイレクト
          logout();
          navigate(adminLoginPath, { replace: true });
          return;
        }
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('メディアの取得に失敗しました');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMedia();
  }, [mediaId, logout, navigate, adminLoginPath]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) {
      return `${bytes} B`;
    } else if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    } else {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMediaUrl = (item: MediaItem): string => {
    return item.cloudfront_url || item.s3_url;
  };

  const handleCopyUrl = async () => {
    if (!media) return;
    
    const url = getMediaUrl(media);
    if (!url) {
      setToastMessage('✗ URLが取得できませんでした');
      setTimeout(() => setToastMessage(null), 3000);
      return;
    }
    
    // 記事作成画面と同じ形式でMarkdownテキストを生成
    const filename = media.original_filename;
    const imageMarkdown = `![${filename}](${url})`;
    
    try {
      await navigator.clipboard.writeText(imageMarkdown);
      setToastMessage('✓ 画像タグをクリップボードにコピーしました');
      setTimeout(() => setToastMessage(null), 3000);
    } catch (err) {
      setToastMessage('✗ 画像タグのコピーに失敗しました');
      setTimeout(() => setToastMessage(null), 3000);
    }
  };

  const handleDelete = async () => {
    if (!media) return;

    if (!window.confirm(`「${media.original_filename}」を削除してもよろしいですか？\n\nこの操作は取り消せません。`)) {
      return;
    }

    try {
      setIsDeleting(true);
      await deleteMedia(media.media_id);
      // 削除成功後、一覧画面に戻る
      navigate(adminMediaListPath);
    } catch (err) {
      console.error('Delete media error:', err);
      if (err instanceof Error && (err as any).isAuthError) {
        // 認証エラーの場合、ログアウトしてログイン画面にリダイレクト
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        window.alert(`削除に失敗しました: ${err.message}`);
      } else {
        window.alert('削除に失敗しました');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="media-detail-container">
        <AdminSidebar />
        <main className="media-detail-main">
          <div className="media-detail-content">
            <div className="media-detail-loading">
              読み込み中...
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="media-detail-container">
        <AdminSidebar />
        <main className="media-detail-main">
          <div className="media-detail-content">
            <div className="media-detail-error">
              {error}
            </div>
            <div className="media-detail-actions">
              <Link to={adminMediaListPath} className="btn-back">
                一覧に戻る
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!media) {
    return (
      <div className="media-detail-container">
        <AdminSidebar />
        <main className="media-detail-main">
          <div className="media-detail-content">
            <div className="media-detail-error">
              メディアが見つかりません
            </div>
            <div className="media-detail-actions">
              <Link to={adminMediaListPath} className="btn-back">
                一覧に戻る
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const mediaUrl = getMediaUrl(media);

  return (
    <div className="media-detail-container">
      <AdminSidebar />
      <main className="media-detail-main">
        <div className="media-detail-content">
          <div className="media-detail-header">
            <h1>メディア詳細</h1>
            <div className="header-actions">
              <Link to={adminMediaListPath} className="btn-back">
                一覧に戻る
              </Link>
            </div>
          </div>

          <div className="media-detail-body">
            <div className="media-detail-preview">
              {media.media_type === 'image' ? (
                <img
                  src={mediaUrl}
                  alt={media.original_filename}
                  className="media-detail-image"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <div className="media-detail-video-placeholder">
                  <span className="video-icon">🎬</span>
                  <span className="video-label">動画</span>
                  <a
                    href={mediaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-view-video"
                  >
                    動画を開く
                  </a>
                </div>
              )}
            </div>

            <div className="media-detail-info">
              <div className="info-section">
                <h2>基本情報</h2>
                <dl className="info-list">
                  <dt>ファイル名</dt>
                  <dd>{media.original_filename}</dd>

                  <dt>種類</dt>
                  <dd>{media.media_type === 'image' ? '画像' : '動画'}</dd>

                  <dt>ファイルサイズ</dt>
                  <dd>{formatFileSize(media.file_size)}</dd>

                  <dt>Content-Type</dt>
                  <dd>{media.content_type}</dd>

                  <dt>アップロード日時</dt>
                  <dd>{formatDate(media.uploaded_at)}</dd>

                  <dt>アップロード者</dt>
                  <dd>{media.uploaded_by}</dd>

                  <dt>使用状況</dt>
                  <dd>
                    {media.used_count > 0 ? (
                      <Link
                        to={`${adminPostsPath}?post_ids=${media.used_in_posts.join(',')}`}
                        className="used-badge used-badge--clickable"
                        title="クリックして使用されている記事一覧を表示"
                      >
                        {media.used_count}件の投稿で使用中
                      </Link>
                    ) : (
                      <span className="unused-badge">未使用</span>
                    )}
                  </dd>
                </dl>
              </div>

              {media.used_count > 0 && media.used_in_posts.length > 0 && (
                <div className="info-section">
                  <h2>使用されている投稿</h2>
                  <ul className="used-posts-list">
                    {media.used_in_posts.map((postId) => (
                      <li key={postId}>{postId}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="info-section">
                <h2>URL情報</h2>
                <dl className="info-list">
                  <dt>CloudFront URL</dt>
                  <dd>
                    {media.cloudfront_url ? (
                      <a
                        href={media.cloudfront_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="url-link"
                      >
                        {media.cloudfront_url}
                      </a>
                    ) : (
                      <span className="no-url">未設定</span>
                    )}
                  </dd>

                  <dt>S3 URL</dt>
                  <dd>
                    <a
                      href={media.s3_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="url-link"
                    >
                      {media.s3_url}
                    </a>
                  </dd>
                </dl>
              </div>
            </div>

            {toastMessage && (
              <div className="media-detail-toast">
                {toastMessage}
              </div>
            )}

            <div className="media-detail-actions">
              <button
                type="button"
                onClick={handleCopyUrl}
                className="btn-copy-url"
              >
                コピー
              </button>
              <a
                href={mediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-view"
              >
                新しいタブで開く
              </a>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting || media.used_count > 0}
                className="btn-delete"
                title={media.used_count > 0 ? '使用中のメディアは削除できません' : '削除'}
              >
                {isDeleting ? '削除中...' : '削除'}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default MediaDetail;


